import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { OpsBeltMark } from "@/components/OpsBeltMark";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useSession } from "@/lib/auth";

/**
 * Landing for an invite link (/accept-invite?token=…). Shows the org you've been
 * invited to, then sends you to sign up with the invited email — the signup hook
 * joins you to that org (you don't get a separate personal org).
 */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { data: session } = useSession();

  const preview = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.invitePreview(token!),
    enabled: !!token,
    retry: false,
  });

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <OpsBeltMark size={32} className="text-primary" />
          <div className="font-display text-lg font-bold tracking-tight">
            Ops<span className="text-primary">Belt</span>
          </div>
        </div>
        <Card className="p-6">{children}</Card>
      </div>
    </div>
  );

  if (!token || preview.isError) {
    return (
      <Shell>
        <h1 className="text-lg font-semibold">Invite not found</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          This invite link is invalid or has expired. Ask whoever invited you for a
          fresh one.
        </p>
      </Shell>
    );
  }
  if (!preview.data) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Loading invite…</p>
      </Shell>
    );
  }

  const inv = preview.data;
  if (inv.status !== "pending") {
    return (
      <Shell>
        <h1 className="text-lg font-semibold">This invite was already used</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You may already be a member of {inv.orgName}. Try signing in.
        </p>
        <Button className="mt-5 w-full" onClick={() => navigate("/login")}>
          Go to sign in
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-lg font-semibold">Join {inv.orgName}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        You've been invited as <b>{inv.role}</b>. Create your OpsBelt account for{" "}
        <b>{inv.email}</b> to join.
      </p>
      {session ? (
        <p className="mt-5 rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground">
          You're signed in as {session.user?.email}. Sign out and accept as{" "}
          {inv.email} to join this organisation.
        </p>
      ) : (
        <Button
          className="mt-5 w-full"
          onClick={() =>
            navigate(`/signup?email=${encodeURIComponent(inv.email)}`)
          }
        >
          Accept &amp; create account
        </Button>
      )}
    </Shell>
  );
}
